"use client";

import { ChevronUp, Clock3, ShieldCheck } from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarHeader,
} from "@/components/ui";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn, filterMenuByAllowedScreens } from "@/utils";
import { IcOxuLogo } from "@/components/icons";
import { IconProps } from "@/components/icons/icon.type";
import { useAuthContext } from "@/components/providers/auth-provider";
import { useMemo } from "react";

// Menu items.
const items = [
  {
    title: "Threat Review",
    url: "/threat-review",
    icon: IcThreatReview,
    iconActive: IcBoldThreatReview,
    subItems: [
      {
        title: "URL Management",
        url: "/threat-review/dashboard",
      },
      {
        title: "Waiting for Approval",
        url: "/threat-review/waiting-approval",
      },
      {
        title: "Categories",
        url: "/threat-review/categories",
      },
    ],
  },
  {
    title: "News & Articles",
    url: "/news-articles",
    icon: IcNewsArticles,
    iconActive: IcBoldNewsArticles,
    subItems: [
      {
        title: "Articles",
        url: "/news-articles/articles",
      },
      {
        title: "Categories",
        url: "/news-articles/categories",
      },
    ],
  },
  {
    title: "Pending Approval",
    url: "/pending-approval",
    icon: Clock3,
    iconActive: IcBoldClock,
  },
  {
    title: "Verification Requests",
    url: "/verification-requests",
    icon: ShieldCheck,
    iconActive: IcBoldShieldCheck,
  },
];

export function AppSidebar() {
  const pathname = usePathname();
  const { allowedScreens } = useAuthContext();

  const visibleMenuItems = useMemo(() => {
    if (!allowedScreens) return [];

    return filterMenuByAllowedScreens(items, allowedScreens);
  }, [allowedScreens]);

  return (
    <Sidebar>
      <SidebarHeader className="p-7">
        <Link
          href={visibleMenuItems && visibleMenuItems[0]?.url ? visibleMenuItems[0].url : "/"}
          className="flex items-center gap-2"
        >
          <div className="flex items-end gap-1">
            <div className="relative flex items-center pb-1">
              <IcOxuLogo className="h-5 md:h-6 w-auto" />
            </div>
            <div className="flex items-baseline font-normal text-xl leading-[1.4] text-[#00152C]">
              TrustOps
            </div>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="pt-1 px-5">
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMenuItems.map(item =>
                item?.subItems?.length ? (
                  <Collapsible key={item.title} defaultOpen className="group collapsible">
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          className={cn(
                            "h-12 flex justify-between px-3 [&>svg]:size-6 font-medium",
                            pathname.includes(item.url) &&
                              "bg-blue-50 text-blue-500 data-[state=open]:hover:bg-blue-50 data-[state=open]:hover:text-blue-500",
                            pathname.includes(item.url) &&
                              "bg-blue-50 text-blue-500 data-[state=closed]:hover:bg-blue-50 data-[state=closed]:hover:text-blue-500",
                            "data-[state=open]:[&>svg]:rotate-180"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {pathname.includes(item.url) ? (
                              <item.iconActive className="w-5 h-5" strokeWidth={1.5} />
                            ) : (
                              <item.icon className="w-5 h-5" strokeWidth={1.5} />
                            )}
                            <span>{item.title}</span>
                          </div>
                          <ChevronUp
                            className={cn(
                              "h-4 w-4 text-gray-400 transition-transform",
                              pathname.includes(item.url) && "text-blue-500"
                            )}
                            strokeWidth={1.5}
                          />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pb-4">
                        {item?.subItems?.map(subItem => (
                          <SidebarMenuSub
                            key={subItem.title}
                            className={cn(
                              "border-l-0 text-[#00152C] font-medium p-0 mx-0",
                              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                              pathname === subItem.url && "text-blue-500 hover:text-blue-500"
                            )}
                          >
                            <SidebarMenuSubItem className="pl-12">
                              <Link href={subItem.url} className="h-8 flex items-center">
                                {subItem.title}
                              </Link>
                            </SidebarMenuSubItem>
                          </SidebarMenuSub>
                        ))}
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem key={item.title}>
                    <Link href={item.url}>
                      <SidebarMenuButton
                        className={cn(
                          "h-12 flex justify-between px-3 [&>svg]:size-6 font-medium",
                          pathname.includes(item.url) &&
                            "bg-blue-50 text-blue-500 data-[state=open]:hover:bg-blue-50 data-[state=open]:hover:text-blue-500",
                          pathname.includes(item.url) &&
                            "bg-blue-50 text-blue-500 data-[state=closed]:hover:bg-blue-50 data-[state=closed]:hover:text-blue-500",
                          "data-[state=open]:[&>svg]:rotate-180"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {pathname.includes(item.url) ? (
                            <item.iconActive className="w-5 h-5" strokeWidth={1.5} />
                          ) : (
                            <item.icon className="w-5 h-5" strokeWidth={1.5} />
                          )}
                          <span>{item.title}</span>
                        </div>
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                )
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function IcThreatReview(props: IconProps) {
  return (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.20361 0.442139C8.94237 -0.145632 9.96769 -0.147304 10.7085 0.436279L10.7095 0.435303L10.9595 0.628662C11.6166 1.11119 12.3685 1.50357 13.2192 1.80347C14.2294 2.15955 15.3809 2.38524 16.6792 2.47339L16.8853 2.4978C17.8955 2.67011 18.6124 3.58472 18.6128 4.61694V8.37866C18.6126 8.73744 18.3212 9.02798 17.9624 9.02808C17.6037 9.02791 17.3132 8.7374 17.313 8.37866V4.61694C17.3127 4.13753 16.9665 3.79567 16.5923 3.77026H16.5913C15.1965 3.67557 13.926 3.43067 12.7866 3.02905C11.8258 2.69031 10.9584 2.24084 10.1899 1.67651C10.186 1.67364 10.1821 1.67068 10.1782 1.66772L9.9165 1.46558L9.90771 1.45874C9.64014 1.24595 9.28076 1.24595 9.01318 1.45874C8.17727 2.12614 7.21513 2.648 6.13428 3.02905C5.10877 3.39054 3.97721 3.62571 2.74463 3.73804H2.73682L2.3374 3.77026H2.32959C1.95521 3.79539 1.6092 4.1373 1.60889 4.61694V9.15991C1.60898 13.964 4.30794 16.7491 6.68213 18.073L6.95947 18.2224L7.25146 18.366H7.25049C7.28936 18.3844 7.32823 18.4021 7.3667 18.4197L7.64697 18.5417L7.89209 18.6394L8.14014 18.7283L8.37549 18.8035H8.37451L8.60205 18.8669L8.80713 18.9158L9.09033 18.9685C9.17576 18.9812 9.2545 18.9891 9.32568 18.9939L9.46045 18.9988L9.59424 18.9939C9.73839 18.9842 9.9129 18.9595 10.1138 18.9158L10.312 18.8679L10.5571 18.8005C10.9027 18.7037 11.2619 18.9052 11.3589 19.2507C11.4555 19.596 11.2538 19.9543 10.9087 20.0515L10.6626 20.1208C10.6547 20.1231 10.6461 20.1248 10.6382 20.1267L10.4038 20.1833L10.3911 20.1863C10.1383 20.2414 9.89763 20.2771 9.67725 20.2917C9.67062 20.2922 9.66338 20.2925 9.65674 20.2927L9.48291 20.2986C9.46803 20.2991 9.45287 20.2991 9.43799 20.2986L9.26416 20.2927C9.25741 20.2925 9.25039 20.2922 9.24365 20.2917C9.02326 20.2771 8.78262 20.2414 8.52979 20.1863L8.51709 20.1833L8.28369 20.1267C8.27571 20.1248 8.26718 20.1231 8.25928 20.1208L8.01318 20.0515L8.00146 20.0486C7.9102 20.0212 7.81744 19.9907 7.72314 19.9587L7.7124 19.9558L7.4458 19.8601C7.4391 19.8577 7.43192 19.8549 7.42529 19.8523L7.1499 19.7429C7.14346 19.7404 7.13673 19.7379 7.13037 19.7351L6.84717 19.6121L6.8374 19.6072C6.78681 19.5842 6.7361 19.5601 6.68506 19.5359L6.67529 19.532L6.3833 19.3875C6.37714 19.3844 6.37081 19.3809 6.36475 19.3777L6.06885 19.2195L6.06006 19.2146C3.3742 17.7208 0.308196 14.5495 0.308105 9.15991V4.61694C0.308473 3.58444 1.02596 2.66977 2.03662 2.4978L2.24268 2.47339L2.63428 2.44214C3.77342 2.33779 4.79459 2.12318 5.70166 1.80347C6.6585 1.46616 7.49029 1.01102 8.20264 0.442139H8.20361ZM15.3208 9.37671C16.4125 9.37674 17.4315 9.56079 18.2017 9.88159C18.5852 10.0414 18.9386 10.2486 19.2056 10.5115C19.4747 10.7767 19.6918 11.1376 19.6919 11.5779C19.6919 11.6015 19.6882 11.625 19.687 11.6482C19.688 11.663 19.6919 11.6781 19.6919 11.6931V17.1541C19.6917 17.5947 19.4729 17.9549 19.2036 18.2175C18.9364 18.478 18.5828 18.6825 18.1997 18.8406C17.4301 19.1581 16.4115 19.3396 15.3208 19.3396C14.2301 19.3396 13.2115 19.1581 12.4419 18.8406C12.0587 18.6825 11.7052 18.478 11.438 18.2175C11.1687 17.9549 10.9489 17.5947 10.9487 17.1541V11.6931C10.9487 11.6788 10.9517 11.6643 10.9526 11.6501C10.9513 11.6264 10.9487 11.6021 10.9487 11.5779C10.9488 11.1377 11.166 10.7766 11.4351 10.5115C11.7019 10.2486 12.0554 10.0415 12.439 9.88159C13.2092 9.56066 14.2289 9.37674 15.3208 9.37671ZM18.2017 16.0232C17.4314 16.344 16.4122 16.5271 15.3208 16.5271C14.2292 16.5271 13.2093 16.3441 12.439 16.0232C12.3744 15.9963 12.3121 15.9649 12.2495 15.9353V17.1541C12.2524 17.1668 12.2681 17.2116 12.3452 17.2869C12.4547 17.3936 12.6471 17.5197 12.937 17.6394C13.5143 17.8776 14.3574 18.0398 15.3208 18.0398C16.2839 18.0398 17.1264 17.8775 17.7036 17.6394C17.9932 17.5199 18.1858 17.3935 18.2954 17.2869C18.3726 17.2116 18.3891 17.1668 18.3921 17.1541V15.9353C18.3294 15.965 18.2664 15.9962 18.2017 16.0232ZM18.2017 13.2742C17.4315 13.595 16.4125 13.778 15.3208 13.7781C14.2289 13.778 13.2092 13.5951 12.439 13.2742C12.3745 13.2473 12.312 13.2168 12.2495 13.1873V14.3318C12.2504 14.3352 12.2533 14.3421 12.2583 14.3523C12.2696 14.3753 12.2953 14.4155 12.3481 14.4675C12.4579 14.5754 12.6498 14.7025 12.939 14.823C13.5156 15.0632 14.3581 15.2273 15.3208 15.2273C16.2833 15.2273 17.125 15.0631 17.7017 14.823C17.991 14.7024 18.1837 14.5755 18.2935 14.4675C18.3461 14.4157 18.3719 14.3753 18.3833 14.3523C18.3881 14.3424 18.3902 14.3353 18.3911 14.3318L18.3921 14.3269V13.1863C18.3293 13.216 18.2665 13.2472 18.2017 13.2742ZM15.3208 10.6775C14.3577 10.6775 13.5155 10.8406 12.939 11.0808C12.6495 11.2014 12.4578 11.3293 12.3481 11.4373C12.2954 11.4892 12.2697 11.5285 12.2583 11.5515C12.253 11.5624 12.2504 11.5697 12.2495 11.573V11.5828C12.2503 11.5861 12.2533 11.5929 12.2583 11.6033C12.2696 11.6262 12.2954 11.6665 12.3481 11.7185C12.4578 11.8264 12.6499 11.9535 12.939 12.074C13.5155 12.3142 14.3577 12.4782 15.3208 12.4783C16.2838 12.4782 17.1251 12.3141 17.7017 12.074C17.9913 11.9533 18.1838 11.8265 18.2935 11.7185C18.3464 11.6663 18.372 11.6262 18.3833 11.6033C18.3883 11.593 18.3903 11.5861 18.3911 11.5828C18.3917 11.5802 18.3921 11.5784 18.3921 11.5779C18.392 11.5772 18.3917 11.5754 18.3911 11.573C18.3903 11.5696 18.3885 11.5621 18.3833 11.5515C18.3719 11.5285 18.3461 11.4891 18.2935 11.4373C18.1838 11.3293 17.9911 11.2014 17.7017 11.0808C17.1252 10.8408 16.2835 10.6775 15.3208 10.6775ZM8.58252 9.49976C8.94135 9.49994 9.23193 9.79127 9.23193 10.1501C9.2318 10.5089 8.94127 10.8004 8.58252 10.8005H4.35693C3.99803 10.8005 3.70667 10.509 3.70654 10.1501C3.70654 9.79116 3.99795 9.49976 4.35693 9.49976H8.58252ZM10.2632 7.49976C10.6221 7.49979 10.9136 7.79118 10.9136 8.15015C10.9134 8.509 10.6221 8.8005 10.2632 8.80054H4.35693C3.99803 8.80054 3.70667 8.50902 3.70654 8.15015C3.70654 7.79116 3.99795 7.49976 4.35693 7.49976H10.2632ZM11.9517 5.49976C12.3106 5.49979 12.6021 5.79118 12.6021 6.15015C12.6019 6.509 12.3105 6.8005 11.9517 6.80054H4.35693C3.99803 6.80054 3.70667 6.50902 3.70654 6.15015C3.70654 5.79116 3.99795 5.49976 4.35693 5.49976H11.9517Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IcBoldThreatReview(props: IconProps) {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.49805 4.4678L1.49805 9.0105C1.49805 14.1107 4.38403 17.0895 6.91524 18.4973L7.2112 18.6557L7.50271 18.7998C7.55086 18.8226 7.59878 18.8449 7.64643 18.8666L7.92908 18.9896L8.20434 19.0988L8.47106 19.1945C8.55841 19.2241 8.64414 19.2515 8.72805 19.2767L8.97415 19.3458L9.20818 19.4018C9.43578 19.4514 9.64352 19.4819 9.82612 19.494L10.0002 19.5L10.1742 19.494C10.3568 19.4819 10.5645 19.4514 10.7921 19.4018L11.0262 19.3458L11.2723 19.2767C11.3562 19.2515 11.4419 19.2241 11.5292 19.1945L11.796 19.0988L12.0712 18.9896L12.3539 18.8666C12.4015 18.8449 12.4494 18.8226 12.4976 18.7998L12.7891 18.6557L13.0851 18.4973C15.6163 17.0895 18.5023 14.1107 18.5023 9.0105V4.4678C18.502 3.67722 17.9211 3.02282 17.175 2.97275C15.8283 2.88134 14.6176 2.64615 13.5427 2.26725C12.6368 1.94791 11.8274 1.52649 11.1145 1.00299L10.8522 0.801163C10.3476 0.399612 9.65268 0.399612 9.1481 0.801163C8.37378 1.41954 7.47678 1.90799 6.45761 2.26725C5.49022 2.60826 4.41277 2.83286 3.22524 2.94107L2.82532 2.97275C2.07923 3.02282 1.49827 3.67722 1.49805 4.4678Z"
        fill="#026EEE"
      />
      <path
        d="M15.1777 8.37695C17.8794 8.37695 20.0707 9.3115 20.0918 10.4678H20.0938V17.8906C20.0933 19.0434 17.8913 19.9775 15.1777 19.9775C12.7406 19.9774 10.6547 19.1919 10.2676 18.2031C10.1584 18.0418 10.2626 10.7609 10.2627 10.6387L10.2764 10.6377C10.2679 10.5871 10.2627 10.5359 10.2627 10.4844C10.263 9.32051 12.4633 8.37706 15.1777 8.37695Z"
        fill="white"
      />
      <path
        d="M11.1069 11.0801V17.2635C11.1069 18.2239 12.9944 19.0026 15.3207 19.0026C17.6471 19.0026 19.5346 18.2239 19.5346 17.2635V11.0801"
        fill="#026EEE"
      />
      <path
        d="M15.1572 12.3582C17.5748 12.3582 19.5346 11.5721 19.5346 10.6024C19.5346 9.63276 17.5748 8.84668 15.1572 8.84668C12.7396 8.84668 10.7798 9.63276 10.7798 10.6024C10.7798 11.5721 12.7396 12.3582 15.1572 12.3582Z"
        fill="#026EEE"
        stroke="white"
        strokeMiterlimit="10"
        strokeLinecap="round"
      />
      <path
        d="M19.9999 14.1768C19.9999 15.0331 17.9041 15.7274 15.3208 15.7274C12.7375 15.7274 10.7319 15.0331 10.7319 14.1768"
        stroke="white"
        strokeMiterlimit="10"
        strokeLinecap="round"
      />
      <path
        d="M4.35693 10H8.58225"
        stroke="white"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.35693 8H10.2633"
        stroke="white"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.35693 6H11.9518"
        stroke="white"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcNewsArticles(props: IconProps) {
  return (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.42296 0.649902H13.6537L18.0383 5.03452V18.1884C18.0383 18.9955 17.384 19.6499 16.5768 19.6499H3.42296C2.61578 19.6499 1.96143 18.9955 1.96143 18.1884V2.11144C1.96143 1.30426 2.61578 0.649902 3.42296 0.649902Z"
        stroke="#444746"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.34619 13.8039H9.26927"
        stroke="#444746"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.34619 10.8805H13.6539"
        stroke="#444746"
        strokeWidth="1.3"
        strokeLinecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.34619 7.95767H13.6539"
        stroke="#444746"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.6538 0.649902V5.03452H18.0384"
        stroke="#444746"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcBoldNewsArticles(props: IconProps) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.42296 0.5H13.6537L18.0383 4.88462V18.0385C18.0383 18.8456 17.384 19.5 16.5768 19.5H3.42296C2.61578 19.5 1.96143 18.8456 1.96143 18.0385V1.96154C1.96143 1.15435 2.61578 0.5 3.42296 0.5Z"
        fill="#026EEE"
      />
      <path
        d="M6.34619 13.6538H9.26927"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.34619 10.7308H13.6539"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.34619 7.80769H13.6539"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path fillRule="evenodd" clipRule="evenodd" d="M13.6538 0.5V4.88462H18.0384" fill="white" />
      <path
        d="M13.6602 1L17.5371 4.87672"
        stroke="#026EEE"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcBoldClock(props: IconProps) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2259_1993)">
        <path
          d="M10.0002 19.1666C15.0628 19.1666 19.1668 15.0626 19.1668 9.99998C19.1668 4.93737 15.0628 0.833313 10.0002 0.833313C4.93755 0.833313 0.833496 4.93737 0.833496 9.99998C0.833496 15.0626 4.93755 19.1666 10.0002 19.1666Z"
          fill="#026EEE"
        />
        <path d="M10.0002 4.49998V9.99998H14.1252" fill="#026EEE" />
        <path
          d="M10 4.5V10H14.125"
          stroke="white"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_2259_1993">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function IcBoldShieldCheck(props: IconProps) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.4978 4.4678L1.4978 9.0105C1.4978 14.1107 4.38378 17.0895 6.915 18.4973L7.21096 18.6557L7.50247 18.7998C7.55062 18.8226 7.59853 18.8449 7.64619 18.8666L7.92883 18.9896L8.2041 19.0988L8.47082 19.1945C8.55817 19.2241 8.6439 19.2515 8.72781 19.2767L8.97391 19.3458L9.20793 19.4018C9.43554 19.4514 9.64328 19.4819 9.82588 19.494L9.99991 19.5L10.1739 19.494C10.3565 19.4819 10.5643 19.4514 10.7919 19.4018L11.0259 19.3458L11.272 19.2767C11.3559 19.2515 11.4417 19.2241 11.529 19.1945L11.7957 19.0988L12.071 18.9896L12.3536 18.8666C12.4013 18.8449 12.4492 18.8226 12.4973 18.7998L12.7889 18.6557L13.0848 18.4973C15.616 17.0895 18.502 14.1107 18.502 9.0105V4.4678C18.5018 3.67722 17.9208 3.02282 17.1747 2.97275C15.8281 2.88134 14.6173 2.64615 13.5425 2.26725C12.6365 1.94791 11.8271 1.52649 11.1142 1.00299L10.852 0.801163C10.3474 0.399612 9.65243 0.399612 9.14786 0.801163C8.37354 1.41954 7.47654 1.90799 6.45736 2.26725C5.48998 2.60826 4.41252 2.83286 3.225 2.94107L2.82508 2.97275C2.07898 3.02282 1.49802 3.67722 1.4978 4.4678Z"
        fill="#026EEE"
      />
      <path
        d="M13.1176 8.01514L9.13434 12.5674L7.42725 10.2913"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
